import {
  CALL_OUTCOMES,
  CALL_OUTCOME_LABELS,
  ESCALATION_KINDS,
  ESCALATION_KIND_LABELS,
  ESCALATION_OUTCOMES,
  ESCALATION_OUTCOME_LABELS,
  ESCALATION_STATUSES,
  ESCALATION_STATUS_LABELS,
  ORDER_RISKS,
  ORDER_RISK_LABELS,
  ORDER_STAGES,
  ORDER_STAGE_LABELS,
  RECEIVABLE_STAGES,
  SHIPMENT_HEALTHS,
  SHIPMENT_HEALTH_LABELS,
  SHIPMENT_STAGES,
  SHIPMENT_STAGE_LABELS,
  type FieldDefinition,
  type FieldOption,
} from "@estelle/domain";

/** The stages and health labels a tracking filter offers: the domain's, in its order. */
const STAGE_OPTIONS: FieldOption[] = SHIPMENT_STAGES.map((value) => ({
  value,
  label: SHIPMENT_STAGE_LABELS[value],
}));
const HEALTH_OPTIONS: FieldOption[] = SHIPMENT_HEALTHS.map((value) => ({
  value,
  label: SHIPMENT_HEALTH_LABELS[value],
}));

/** Normalised courier keys: the store writes one courier several ways (#321). */
const COURIER_OPTIONS: FieldOption[] = [
  { value: "delhivery", label: "Delhivery" },
  { value: "bluedart", label: "Bluedart" },
  { value: "dtdc", label: "DTDC" },
  { value: "xpressbees", label: "Xpressbees" },
  { value: "other", label: "Other" },
];

/**
 * The fields the Orders module offers to the filter builder, the column
 * chooser and the sort control.
 *
 * Declared rather than derived from `Order`: a type says what a row holds,
 * not which of it is worth filtering on, what to call it in front of a user,
 * or which enum members exist. Covers every filter FR-ORD-2 names.
 *
 * Order matters: it is the column order the table renders left to right.
 *
 * Six columns by default (#643): Order, Customer (name over city), Items,
 * Amount (total over COD or Prepaid), Stage, Placed. Stage is the one status;
 * Confirmation, Payment, Fulfilment and Delivery status, and the rest, are in
 * the column chooser. With eighteen on, four of them coloured pills, no single
 * pill stood out and a row that needed action looked like one that did not.
 */
export const ORDER_FIELDS: FieldDefinition[] = [
  { name: "orderNumber", label: "Order", type: "text", sortable: true },
  { name: "customerName", label: "Customer", type: "text", sortable: true },
  { name: "itemCount", label: "Items", type: "number", sortable: true },
  { name: "total", label: "Amount", type: "money", sortable: true },
  /*
    Stage (#465): the order's one status from placed to delivered or received
    back, derived by the server (`stage/sql.ts`), not copied from Shopify. Its
    heading opens the legend of the whole flow (#643).
  */
  {
    name: "stage",
    label: "Stage",
    type: "enum",
    sortable: true,
    options: ORDER_STAGES.map((value) => ({
      value,
      label: ORDER_STAGE_LABELS[value],
    })),
  },
  { name: "placedAt", label: "Placed", type: "date", sortable: true },
  {
    // Whether a person set the stage by hand (#591): the Stage column's
    // "manual" marker, as a filter.
    name: "stageManual",
    label: "Stage set by hand",
    type: "boolean",
    defaultHidden: true,
  },
  {
    // How the COD confirmation call went (#465).
    name: "confirmation",
    label: "Confirmation",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: CALL_OUTCOMES.map((value) => ({
      value,
      label: CALL_OUTCOME_LABELS[value],
    })),
  },
  {
    name: "risk",
    label: "Risk",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: ORDER_RISKS.map((value) => ({
      value,
      label: ORDER_RISK_LABELS[value],
    })),
  },
  {
    name: "overdue",
    label: "Overdue confirmation",
    type: "boolean",
    defaultHidden: true,
  },
  {
    name: "notShipped",
    label: "Ready to ship, waiting over 24 hours",
    type: "boolean",
    defaultHidden: true,
  },
  {
    name: "sourceApp",
    label: "Channel",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "estelleluxe x FlexyPe", label: "FlexyPe" },
      { value: "Estelle Luxe<>Gokwik", label: "GoKwik" },
      { value: "Draft Orders", label: "Draft order" },
      { value: "Online Store", label: "Online Store" },
      { value: "Marketplace Connect", label: "Amazon (Marketplace Connect)" },
    ],
  },
  {
    name: "financialStatus",
    label: "Payment status",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "pending", label: "Pending" },
      { value: "authorized", label: "Authorized" },
      { value: "partially_paid", label: "Partially paid" },
      { value: "paid", label: "Paid" },
      { value: "partially_refunded", label: "Partially refunded" },
      { value: "refunded", label: "Refunded" },
      { value: "voided", label: "Voided" },
    ],
  },
  {
    name: "fulfilmentStatus",
    label: "Fulfilment status",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "partial", label: "Partial" },
      { value: "fulfilled", label: "Fulfilled" },
      { value: "restocked", label: "Restocked" },
    ],
  },
  {
    name: "deliveryStatus",
    label: "Delivery status",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    // Shopify's own wording for the order list's delivery column, not the
    // domain's `ShipmentStatus` labels the Shipments section uses (#350) —
    // the two sections describe the same states to two different readers.
    options: [
      { value: "pending", label: "Tracking added" },
      { value: "in_transit", label: "In transit" },
      { value: "out_for_delivery", label: "Out for delivery" },
      { value: "ready_for_pickup", label: "Ready for pickup" },
      { value: "attempted_delivery", label: "Delivery attempted" },
      { value: "not_delivered", label: "Not delivered" },
      { value: "delivered", label: "Delivered" },
      { value: "failed", label: "Failed delivery" },
      { value: "returned", label: "Returned" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
  {
    name: "deliveryMethod",
    label: "Delivery method",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
  // In the chooser, out of the default view (#354) — pills make every row
  // tall, which is why Shopify's own list keeps Tags out too.
  {
    name: "tags",
    label: "Tags",
    type: "tag",
    defaultHidden: true,
    showAllTags: true,
  },
  /*
    Shipment tracking (#527). An order has up to three shipments and one row,
    so these are the one shipment that speaks for it: an open one needing
    attention before a finished one, so a delivered parcel never hides its
    stuck sibling (`routes/orders.ts`, `focusShipment`). The courier's own
    status stays in "Delivery status" above; these are the ERP's, derived from
    the scans, drawn as a stage badge and a health label.

    Out of the default columns on purpose: Orders already has ten, and most of
    its rows are delivered, so a column of badges would say "Delivered" down the
    page. What a person needs from this list is the exception, and that is shown
    where it is read — a "No update" or "Delayed" label beside Delivery status,
    on a row that needs it and nowhere else. The columns are in the chooser for
    anyone who wants them.
  */
  {
    name: "shipmentStage",
    label: "Tracking stage",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: STAGE_OPTIONS,
  },
  {
    name: "shipmentHealth",
    label: "Tracking health",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: HEALTH_OPTIONS,
  },
  {
    name: "lastScanAt",
    label: "Last tracking update",
    type: "datetime",
    sortable: true,
    defaultHidden: true,
  },
  /*
    The courier of the shipment that speaks for the order (#633). Filter-only:
    a row does not carry it, so there is no column to show; the filter is
    answered by the API and the fixture adapter, which hold the shipments.
  */
  {
    name: "courier",
    label: "Courier",
    type: "enum",
    displayable: false,
    options: COURIER_OPTIONS,
  },
  {
    name: "attemptCount",
    label: "Delivery attempts",
    type: "number",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "expectedDeliveryDate",
    label: "Expected delivery",
    type: "date",
    sortable: true,
    defaultHidden: true,
  },
  // The state is under the customer's name in the default view (#643).
  {
    name: "shippingProvinceCode",
    label: "Shipping state",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "shippingCountryCode",
    label: "Shipping country",
    type: "text",
    defaultHidden: true,
  },
  {
    name: "paymentGateway",
    label: "Payment gateway",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    // Normalised keys — COD arrives spelled two ways (#321).
    options: [
      { value: "cod", label: "Cash on delivery" },
      { value: "cashfree", label: "Cashfree" },
      { value: "razorpay", label: "Razorpay" },
      { value: "gokwik_cards", label: "GoKwik cards" },
      { value: "gokwik_upi", label: "GoKwik UPI" },
      { value: "manual", label: "Manual" },
      { value: "amazon", label: "Amazon" },
    ],
  },
  {
    name: "discountCode",
    label: "Discount code",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "cancelledAt",
    label: "Cancelled",
    type: "date",
    sortable: true,
    defaultHidden: true,
  },
  // #445: a task linked to the order that is neither done nor cancelled. In
  // the chooser and the filter builder, out of the default view — it is a
  // question to ask of the list, not a column most rows differ on.
  {
    name: "hasOpenFollowUp",
    label: "Has open follow-up",
    type: "boolean",
    defaultHidden: true,
  },
  // #448: an ERP-only comment that has not been deleted, and one that mentions
  // the signed-in user. Questions to ask of the list, so out of the default
  // view; the server answers them only for someone who may open the order.
  {
    name: "hasComments",
    label: "Has comments",
    type: "boolean",
    defaultHidden: true,
  },
  {
    name: "mentionsMe",
    label: "Mentions me",
    type: "boolean",
    defaultHidden: true,
  },
  {
    name: "warehouse",
    label: "Warehouse",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
  /*
    A returned parcel of this order has been received by the team (#529): the
    ERP's own record, from a receipt, not anything Shopify says. A column in the
    chooser and a filter, out of the default view since most orders never come
    back; the order's page carries the badge.
  */
  {
    name: "rtoReceived",
    label: "RTO received",
    type: "boolean",
    defaultHidden: true,
  },
  /*
    Any shipment of the order has an open escalation (#702): the parcel failed
    delivery, went quiet or was lost and nobody has closed it. A column in the
    chooser, a filter and a sort; on a row that has one, an "Escalated" label
    stands beside the delivery status, and nowhere else.
  */
  {
    name: "escalated",
    label: "Escalated",
    type: "boolean",
    sortable: true,
    defaultHidden: true,
  },
  /*
    Cash on delivery or prepaid (#449), derived from the payment gateway
    rather than from the `COD` and `prepaid` tags, which only the checkout app
    writes and which miss GoKwik, Amazon and manual payments. `paymentKindOf`
    in `@estelle/domain` is the rule; the API says the same in SQL. Out of the
    default columns, since the list already shows the gateway's own name.
  */
  {
    name: "payment",
    // Not "Payment": next to "Payment status" and "Payment gateway" that is
    // three fields a person has to tell apart by a word.
    label: "Payment method (COD / prepaid)",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "cod", label: "Cash on delivery" },
      { value: "prepaid", label: "Prepaid" },
    ],
  },
  /*
    The products on an order's lines (#449) — "orders containing" these.
    Filter-only: a row holds no line items, so there is nothing for a column to
    show, and `displayable: false` keeps it out of the column chooser, the
    default view and the export rather than offering a column of dashes. Its
    options are the catalogue's products, supplied by the section at runtime
    (`fieldOptions`) the way a store's collections are; the value is the title,
    as a collection's is.
  */
  { name: "product", label: "Product", type: "tag", displayable: false },
];

/**
 * The permission each protected order field needs, by field name (#448).
 *
 * The comment fields are answered only for someone who may open the order: a
 * thread is part of its page, and "Has comments" would tell a caller who
 * cannot read it that one exists. For anyone else the API answers `false` and
 * ignores a filter that names them, and `SectionPage` does not offer them at
 * all (`SectionUi.fieldPermissions`) — a courtesy, never the enforcement.
 */
export const ORDER_FIELD_PERMISSIONS: Record<string, string> = {
  hasComments: "orders.detail.view",
  mentionsMe: "orders.detail.view",
};

/**
 * Fields for the Shipments section (#58, FR-ORD-6).
 *
 * A shipment has no vocabulary of its own in the module sense — it borrows
 * `orderNumber`, the two destination fields and `placedAt` from the order it
 * belongs to, because "which order", "where is it going" and "when was it
 * ordered" are what someone filtering a courier queue actually asks.
 * `listShipments` joins them.
 */
export const SHIPMENT_FIELDS: FieldDefinition[] = [
  { name: "orderNumber", label: "Order", type: "text", sortable: true },
  {
    name: "courier",
    label: "Courier",
    type: "enum",
    sortable: true,
    options: COURIER_OPTIONS,
  },
  /*
    One status per row (#642): Stage, with Health drawn beside it only when it
    needs a look. Shopify's own Status and the Health column stay in the
    chooser and the filter builder; on by default they were three coloured
    columns saying the same thing.
  */
  {
    name: "stage",
    label: "Stage",
    type: "enum",
    sortable: true,
    options: STAGE_OPTIONS,
  },
  {
    name: "status",
    label: "Shopify status",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "pending", label: "Pending" },
      { value: "in_transit", label: "In transit" },
      { value: "out_for_delivery", label: "Out for delivery" },
      { value: "ready_for_pickup", label: "Ready for pickup" },
      { value: "attempted_delivery", label: "Delivery attempted" },
      { value: "not_delivered", label: "Not delivered" },
      { value: "delivered", label: "Delivered" },
      { value: "failed", label: "Failed" },
      { value: "returned", label: "Returned" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
  {
    name: "health",
    label: "Health",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: HEALTH_OPTIONS,
  },
  { name: "trackingNumber", label: "Tracking number", type: "text" },
  {
    name: "destinationState",
    label: "Destination state",
    type: "text",
    sortable: true,
  },
  { name: "destinationCountry", label: "Destination country", type: "text" },
  // The order's own placed date (#491), ahead of Shipped and Delivered so the
  // three read in the order they happened.
  { name: "placedAt", label: "Order placed", type: "date", sortable: true },
  { name: "shippedAt", label: "Shipped", type: "date", sortable: true },
  { name: "deliveredAt", label: "Delivered", type: "date", sortable: true },
  /*
    Shipment tracking (#527): where the parcel is, from its scans, and whether
    it needs a look. `status` above is Shopify's word for it; Stage and Health
    are ours, derived from the scans, and are on by default here — a courier
    queue is read for exactly these. The other three stay in the chooser and
    the filter builder: they answer a question, they do not differ row by row
    enough to earn a column of every list. Stage and Health sit right after
    Status, where they are read against it.
  */
  {
    name: "lastScanAt",
    label: "Last tracking update",
    type: "datetime",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "attemptCount",
    label: "Attempts",
    type: "number",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "expectedDeliveryDate",
    label: "Expected delivery",
    type: "date",
    sortable: true,
    defaultHidden: true,
  },
  // Whether the parcel has been received back (#529). A shipment is received
  // once; "RTO delivered, not yet received" asks for the ones that have not.
  {
    name: "rtoReceived",
    label: "RTO received",
    type: "boolean",
    defaultHidden: true,
  },
  // This parcel has an open escalation (#702).
  {
    name: "escalated",
    label: "Escalated",
    type: "boolean",
    sortable: true,
    defaultHidden: true,
  },
];

/**
 * Fields for Returns to receive (#529): a failed or returning shipment nobody
 * has received yet.
 *
 * Its own vocabulary, not Shipments': a section's fields describe the rows it
 * renders, and these rows are a parcel in the warehouse's terms — how long it
 * has been coming back, whether the courier says it is back, how many items.
 * Every field is projected by the API and by the fixture adapter alike.
 */
export const RETURN_FIELDS: FieldDefinition[] = [
  { name: "orderNumber", label: "Order", type: "text", sortable: true },
  {
    name: "courier",
    label: "Courier",
    type: "enum",
    sortable: true,
    options: SHIPMENT_FIELDS.find((field) => field.name === "courier")?.options,
  },
  { name: "trackingNumber", label: "Tracking number", type: "text" },
  {
    name: "stage",
    label: "Stage",
    type: "enum",
    sortable: true,
    // The ones a parcel can be received from, in the order of the journey.
    options: STAGE_OPTIONS.filter((option) =>
      (RECEIVABLE_STAGES as readonly string[]).includes(option.value),
    ),
  },
  {
    name: "returningDays",
    label: "Returning for (days)",
    type: "number",
    sortable: true,
  },
  {
    name: "rtoDelivered",
    label: "Courier says RTO delivered",
    type: "boolean",
  },
  { name: "itemCount", label: "Items", type: "number", sortable: true },
  {
    // Whether a person added the return by hand (#594).
    name: "manual",
    label: "Added manually",
    type: "boolean",
    defaultHidden: true,
  },
  {
    name: "shopifyStatus",
    label: "Shopify status",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: SHIPMENT_FIELDS.find((field) => field.name === "status")?.options,
  },
  {
    name: "warehouse",
    label: "Shipped from",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "payment",
    label: "Payment method (COD / prepaid)",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "cod", label: "Cash on delivery" },
      { value: "prepaid", label: "Prepaid" },
    ],
  },
];

/**
 * Fields for the Recent Changes section (#59, FR-ORD-15).
 *
 * `before`/`after` are deliberately not here: they are arbitrary `Json`,
 * which has no ordering and no case-insensitive text form worth filtering
 * on, and the row still renders them (see `OrderHistory.tsx`'s formatter in
 * `format.ts`) — they are just not part of what the filter builder offers.
 */
export const CHANGE_FIELDS: FieldDefinition[] = [
  { name: "orderNumber", label: "Order", type: "text", sortable: true },
  { name: "occurredAt", label: "When", type: "datetime", sortable: true },
  {
    name: "eventType",
    label: "Change type",
    type: "enum",
    sortable: true,
    options: [
      { value: "created", label: "Created" },
      { value: "tag_added", label: "Tag added" },
      { value: "tag_removed", label: "Tag removed" },
      { value: "note_changed", label: "Note changed" },
      { value: "financial_status_changed", label: "Payment status changed" },
      {
        value: "fulfilment_status_changed",
        label: "Fulfilment status changed",
      },
      { value: "shipment_created", label: "Shipment created" },
      { value: "shipment_updated", label: "Shipment updated" },
      { value: "cancelled", label: "Cancelled" },
      { value: "refunded", label: "Refunded" },
      { value: "address_changed", label: "Address changed" },
      { value: "line_item_changed", label: "Line item changed" },
      { value: "rto_received", label: "Return received" },
      { value: "possible_double_restock", label: "Possible double restock" },
      { value: "return_added_manually", label: "Return added by hand" },
      { value: "stage_override_set", label: "Stage set by hand" },
      { value: "stage_override_cleared", label: "Stage back to automatic" },
    ],
  },
  /*
    What changed, from and to (#642): the screen draws "Status: Pending →
    Shipped" from the event; filtering still matches the raw field key.
  */
  { name: "field", label: "Change", type: "text" },
  // "Shopify" for what the store did, a name for an ERP write (#642).
  { name: "actorUserId", label: "Changed by", type: "reference" },
  // Said by "Changed by" already, so in the chooser rather than the row.
  {
    name: "source",
    label: "Source",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: [
      { value: "channel", label: "Shopify" },
      { value: "erp", label: "ERP" },
    ],
  },
];

/**
 * Fields for Orders > Escalations (#702): one row per shipment and kind, so an
 * order whose first parcel was lost and whose replacement failed delivery shows
 * twice, each with its own AWB. Every field is projected by the API and by the
 * fixture adapter alike. None names a customer: the screen, its copy actions,
 * its CSV and every email are built from these and nothing else.
 */
export const ESCALATION_FIELDS: FieldDefinition[] = [
  { name: "orderNumber", label: "Order", type: "text", sortable: true },
  { name: "awb", label: "AWB", type: "text", sortable: true },
  {
    name: "courier",
    label: "Courier",
    type: "enum",
    sortable: true,
    options: COURIER_OPTIONS,
  },
  {
    name: "kind",
    label: "Escalation",
    type: "enum",
    sortable: true,
    options: ESCALATION_KINDS.map((value) => ({
      value,
      label: ESCALATION_KIND_LABELS[value],
    })),
  },
  {
    name: "level",
    label: "Level",
    type: "number",
    sortable: true,
    defaultHidden: true,
  },
  { name: "lastScanAt", label: "Last scan", type: "datetime", sortable: true },
  { name: "attempts", label: "Attempts", type: "number", sortable: true },
  { name: "daysOpen", label: "Days open", type: "number", sortable: true },
  { name: "orderValue", label: "Value", type: "money", sortable: true },
  {
    name: "payment",
    label: "COD / prepaid",
    type: "enum",
    sortable: true,
    options: [
      { value: "cod", label: "Cash on delivery" },
      { value: "prepaid", label: "Prepaid" },
    ],
  },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: ESCALATION_STATUSES.map((value) => ({
      value,
      label: ESCALATION_STATUS_LABELS[value],
    })),
  },
  {
    name: "openedAt",
    label: "Opened",
    type: "datetime",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "lastSentAt",
    label: "Last sent",
    type: "datetime",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "outcome",
    label: "Outcome",
    type: "enum",
    sortable: true,
    defaultHidden: true,
    options: ESCALATION_OUTCOMES.map((value) => ({
      value,
      label: ESCALATION_OUTCOME_LABELS[value],
    })),
  },
];
